import React from 'react';

const DEFAULT_PAGE_SIZE = parseInt(process.env.REACT_APP_PAGE_SIZE) || 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, DEFAULT_PAGE_SIZE, 50].filter(
  (v, i, arr) => arr.indexOf(v) === i
).sort((a, b) => a - b);

/**
 * Calculates page number array for visible pagination controls centered around the active page.
 *
 * @function getVisiblePages
 * @param {number} current - Current active page number.
 * @param {number} total - Total page count.
 * @returns {Array<number>} Array of page numbers to render.
 */
function getVisiblePages(current, total) {
  const max = 7;
  const safe = Math.max(1, total);
  const half = Math.floor(max / 2);
  const start = Math.max(1, Math.min(current - half, safe - max + 1));
  const end = Math.min(safe, start + max - 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Pagination Component.
 * Server-side pagination bar with first/prev/next/last navigation buttons and page size selector.
 * Reads default page limit from REACT_APP_PAGE_SIZE environment variable.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {number} [props.page=1] - Current active page number.
 * @param {number} [props.totalPages=1] - Total number of available pages.
 * @param {number} [props.total=0] - Total count of matching records across all pages.
 * @param {number} [props.limit] - Page limit size. Defaults to `REACT_APP_PAGE_SIZE` or 20.
 * @param {Function} props.onPage - Callback invoked when user selects a page (`onPage(newPage)`).
 * @param {Function} [props.onLimit] - Optional callback invoked when user changes page size (`onLimit(newLimit)`).
 * @param {Array<number>} [props.pageSizeOptions] - Page size options list. Defaults to `[10, DEFAULT_PAGE_SIZE, 50]`.
 * @returns {JSX.Element} Pagination control bar.
 */
export default function Pagination({
  page = 1,
  totalPages = 1,
  total = 0,
  limit = DEFAULT_PAGE_SIZE,
  onPage,
  onLimit,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}) {
  const safe = Math.max(1, totalPages);
  const pages = getVisiblePages(page, safe);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 10,
      marginTop: 16,
      paddingTop: 14,
      borderTop: '1px solid var(--gray-4, #e5e7eb)',
    }}>
      {/* Count label */}
      <span style={{ fontSize: 13, color: 'var(--gray-1, #6b7280)', whiteSpace: 'nowrap' }}>
        {total === 0 ? 'No records' : `Showing ${from}–${to} of ${total.toLocaleString('en-US')}`}
      </span>

      {/* Page buttons */}
      <div className="pagination" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* First */}
        <button
          className="page-btn"
          onClick={() => onPage(1)}
          disabled={page === 1}
          title="First page"
        >«</button>

        {/* Prev */}
        <button
          className="page-btn"
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
        >‹</button>

        {/* Ellipsis left */}
        {pages[0] > 1 && (
          <span style={{ padding: '0 4px', color: 'var(--gray-1)' }}>…</span>
        )}

        {pages.map(p => (
          <button
            key={p}
            className={`page-btn${page === p ? ' active' : ''}`}
            onClick={() => onPage(p)}
          >{p}</button>
        ))}

        {/* Ellipsis right */}
        {pages[pages.length - 1] < safe && (
          <span style={{ padding: '0 4px', color: 'var(--gray-1)' }}>…</span>
        )}

        {/* Next */}
        <button
          className="page-btn"
          onClick={() => onPage(page + 1)}
          disabled={page === safe}
        >›</button>

        {/* Last */}
        <button
          className="page-btn"
          onClick={() => onPage(safe)}
          disabled={page === safe}
          title="Last page"
        >»</button>
      </div>

      {/* Page size selector */}
      {onLimit && (
        <select
          className="form-control"
          style={{ width: 'auto', fontSize: 13, padding: '4px 8px' }}
          value={limit}
          onChange={e => onLimit(Number(e.target.value))}
        >
          {pageSizeOptions.map(n => (
            <option key={n} value={n}>{n} per page</option>
          ))}
        </select>
      )}
    </div>
  );
}

export { DEFAULT_PAGE_SIZE };
